/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Alert, CircularProgress, Stack, Typography, Switch, FormControlLabel, TextField } from '@mui/material';
import { shallowEqual } from 'react-redux';
// Components
import { AttendedAppointment } from './Attended';
import { OpenDialogPopup, OpenAttendedPopup, OpenUnAttendedPopup } from './fullScreenPopUps';
import { PersonalDetails, TreatmentAndAppointment, PaymentOption, MessageAndReminder } from '../sections/@dashboard/general/app';
import { DentallyPaymentConfirmation, DentallyBookingForm, DentallyBookingFormPayment, DentallyBookingSuccess, DentallyTreatmentAvailabilityWizardStep, BookingModeToggle } from './dentally';
import { PmsAvailabilityWizardStep, PmsBookingConfirmStep } from './pms';
import Iconify from './Iconify';
// Redux
import { setCurrentTreatmentDataGlobal } from '../redux/slices/multiTreatment';
import { clearLead, updateSteps, createNewLead, updateLeadDetail, addNotes, updateSendReminderDuringLeadCreate } from '../redux/slices/lead';
import { updateError } from '../redux/slices/user';
import { closeCreateLeadDialog, openCreateLeadDialog, closeDentallyBookingDialog } from '../redux/slices/createLeadDialog';
import { dispatch, useSelector, store } from '../redux/store';
import { getDentallyConfigs } from '../redux/slices/practiceIntegration';
// hooks
import { useRecordAttendToggle, useUnRecordAttendToggle } from '../hooks';
import { useDentallyBooking } from '../hooks/useDentallyBooking';
import { localStorageRemoveItem } from '../utils/storage-available';
import { sectionCardStyle, sectionLabelStyle } from '../styles/DentallyBookingStyle';

// ── Dentally wizard step wrappers ──────────────────────────────────────────────
// Defined outside the main component so their identity is stable across renders.


const BOOKING_PROGRESS_STEPS = [
    { index: 1, label: 'Creating lead' },
    { index: 2, label: 'Adding treatment' },
    { index: 3, label: 'Creating appointment' },
    { index: 4, label: 'Booking in Dentally' },
    { index: 5, label: 'Processing payment' },
];

const DentallyPaymentWizardStep = forwardRef(({
    dentallySlot, exportTrigger, onExportTriggerChange, dentallyPayment, onPaymentChange,
    dentallySubmit, dentallyTreatmentName, dentallyReason, dentallyAssignees,
    dentallyLoading, dentallyResult, dentallyError, bookingStep, onClose,
}, ref) => {
    const personalDetail = useSelector((state) => state.lead?.newLeadDetail?.personalDetail || {});
    const treatmentDetail = useSelector((state) => state.lead?.newLeadDetail?.treatmentDetail || {});
    const { practiceId: currentPracticeId } = useSelector((state) => state.practiceTreatmentList);

    const resolvedEmail = personalDetail.lead_contact?.email || personalDetail.email || '';
    const resolvedPhone = personalDetail.lead_contact?.phone || personalDetail.phoneNumber || personalDetail.phone || '';
    const [contactEmail, setContactEmail] = useState(resolvedEmail);
    const [contactPhone, setContactPhone] = useState(resolvedPhone);

    const isPaymentRequired = exportTrigger === 'PAYMENT';
    const linkChannel = dentallyPayment.paymentLinkChannel || 'email';
    const needsEmail = dentallyPayment.paymentOption === 'link' && (linkChannel === 'email' || linkChannel === 'both');
    const needsPhone = dentallyPayment.paymentOption === 'link' && (linkChannel === 'whatsapp' || linkChannel === 'both');
    const canConfirm =
        dentallySlot &&
        !dentallySlot._manual &&
        !dentallyLoading &&
        !dentallyResult?.success &&
        (!isPaymentRequired || dentallyPayment.paymentOption !== 'none') &&
        (!needsEmail || contactEmail.trim()) &&
        (!needsPhone || contactPhone.trim());

    const handleConfirm = useCallback(async () => {
        if (!canConfirm) return;
        const startIso = dentallySlot.start_time;
        const finishIso = dentallySlot.finish_time;
        const durationMs = new Date(finishIso) - new Date(startIso);
        const durationMinutes = Math.round(durationMs / 60000) || 30;

        await dentallySubmit({
            firstName: personalDetail.firstName || personalDetail.first_name || '',
            lastName: personalDetail.lastName || personalDetail.last_name || '',
            phone: contactPhone.trim() || resolvedPhone,
            email: contactEmail.trim() || resolvedEmail,
            salutation: personalDetail.salutation || '',
            dob: personalDetail.dob || '',
            sex: personalDetail.sex || '',
            postcode: personalDetail.lead_address?.postcode || personalDetail.postcode || '',
            addressLine1: personalDetail.lead_address?.address_line_1 || personalDetail.addressLine1 || '',
            treatmentName: dentallyTreatmentName || treatmentDetail?.treatment || treatmentDetail?.name || '',
            practitionerId: dentallySlot._queried_practitioner_id || dentallySlot.practitioner_id,
            reason: dentallyReason || 'Exam',
            dateTime: new Date(startIso),
            durationMinutes,
            method: 'IN_PRACTICE',
            assignees: dentallyAssignees?.map((a) => a.id) || [],
            paymentOption: dentallyPayment.paymentOption || 'none',
            paymentAmount: dentallyPayment.paymentAmount,
            paymentType: dentallyPayment.paymentType || 'DEPOSIT',
            receivedMode: dentallyPayment.receivedMode,
            paymentLinkChannel: dentallyPayment.paymentLinkChannel || 'email',
        });
    }, [canConfirm, dentallySlot, dentallySubmit, personalDetail, contactPhone, contactEmail, resolvedPhone, resolvedEmail, dentallyTreatmentName, dentallyReason, treatmentDetail, dentallyAssignees, dentallyPayment]);

    useImperativeHandle(ref, () => ({
        submit() {
            if (!canConfirm) return null;
            handleConfirm();
            return null; // Don't advance wizard — success screen replaces content
        },
    }), [canConfirm, handleConfirm]);

    // ── Success view ───────────────────────────────────────────────────────────
    if (dentallyResult?.success) {
        return (
            <DentallyBookingSuccess
                result={dentallyResult}
                practiceId={currentPracticeId}
                onClose={onClose}
            />
        );
    }

    // ── Progress / loading view ────────────────────────────────────────────────
    if (dentallyLoading) {
        const currentIdx = bookingStep?.index || 0;
        const renderStepIcon = (index) => {
            if (currentIdx > index) return <Iconify icon='mdi:check-circle' width={18} sx={{ color: 'success.main', flexShrink: 0 }} />;
            if (currentIdx === index) return <CircularProgress size={16} thickness={5} sx={{ flexShrink: 0, color: 'success.main' }} />;
            return <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'divider', flexShrink: 0 }} />;
        };
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={44} sx={{ mb: 2, color: 'success.main' }} />
                <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
                    Booking in progress…
                </Typography>
                {bookingStep?.label && (
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                        {bookingStep.label}
                    </Typography>
                )}
                <Box sx={{ textAlign: 'left', maxWidth: 260, mx: 'auto' }}>
                    {BOOKING_PROGRESS_STEPS.map(({ index, label }) => (
                        <Stack key={index} direction='row' alignItems='center' spacing={1} sx={{ mb: 0.75 }}>
                            {renderStepIcon(index)}
                            <Typography variant='body2' sx={{
                                color: currentIdx >= index ? 'text.primary' : 'text.disabled',
                                fontWeight: currentIdx === index ? 600 : 400,
                            }}>
                                {label}
                            </Typography>
                        </Stack>
                    ))}
                </Box>
            </Box>
        );
    }

    // ── Normal booking form ────────────────────────────────────────────────────
    return (
        <Box>
            {/* Mode toggle — switch between payment-required and payment-optional */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                mb: 2, px: 1.5, py: 1, borderRadius: 1.5,
                bgcolor: isPaymentRequired ? 'rgba(255,171,0,0.08)' : 'rgba(0,171,85,0.06)',
                border: '1px solid',
                borderColor: isPaymentRequired ? 'rgba(255,171,0,0.24)' : 'rgba(0,171,85,0.16)',
            }}>
                <Stack direction='row' alignItems='center' spacing={1}>
                    <Iconify
                        icon={isPaymentRequired ? 'mdi:cash-lock' : 'mdi:calendar-check'}
                        width={18}
                        sx={{ color: isPaymentRequired ? '#FFAB00' : '#00AB55' }}
                    />
                    <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
                        {isPaymentRequired ? 'Payment required before booking' : 'Book first — payment optional'}
                    </Typography>
                </Stack>
                <FormControlLabel
                    control={
                        <Switch
                            size='small'
                            checked={isPaymentRequired}
                            onChange={(e) => onExportTriggerChange(e.target.checked ? 'PAYMENT' : 'APPOINTMENT')}
                        />
                    }
                    label={<Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Require payment</Typography>}
                    sx={{ m: 0 }}
                />
            </Box>

            <DentallyBookingFormPayment
                values={dentallyPayment}
                onChange={(field, val) => onPaymentChange((prev) => ({ ...prev, [field]: val }))}
                errors={{}}
            />
            {isPaymentRequired && dentallyPayment.paymentOption === 'none' && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                    Payment required before appointment is confirmed.
                </Alert>
            )}

            {/* Contact details card — visible when sending payment link */}
            {dentallyPayment.paymentOption === 'link' && (
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon='mdi:account-circle-outline' width={15} sx={{ color: '#078DEE' }} />
                        Contact Details
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
                        Payment link will be sent to the email below. Verify details are correct.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <TextField
                            size='small'
                            label='Email'
                            type='email'
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Iconify icon='mdi:email-outline' width={18} sx={{ color: 'text.disabled', mr: 1 }} />
                                ),
                            }}
                        />
                        <TextField
                            size='small'
                            label='Phone'
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Iconify icon='mdi:phone-outline' width={18} sx={{ color: 'text.disabled', mr: 1 }} />
                                ),
                            }}
                        />
                    </Box>
                    {!contactEmail.trim() && (
                        <Alert severity='warning' sx={{ mt: 1.5, mb: 0 }}>
                            Email is required to send a payment link.
                        </Alert>
                    )}
                </Box>
            )}

            {dentallyError && <Alert severity='error' sx={{ mb: 2 }}>{dentallyError.message}</Alert>}
        </Box>
    );
});

// ── Main component ─────────────────────────────────────────────────────────────

const CreateLeadDialogWrapper = () => {
    const openContacts = useSelector((state) => state.createLeadDialog.open);
    const dentallyBookingOpen = useSelector((state) => state.createLeadDialog.dentallyBookingOpen);

    const {
        attendance,
        markAsComplete,
        followUpLead,
        consentLink,
        isDentallyBooking,
    } = useSelector((state) => ({
        attendance: state.lead?.attendance,
        markAsComplete: state.lead?.markAsComplete,
        followUpLead: state.lead?.followUpLead,
        consentLink: state.lead?.consentLink,
        isDentallyBooking: state.lead?.isDentallyBooking,
    }), shallowEqual);
    const { dentallyEnabledGlobal } = useSelector((state) => state.practice);
    const { pmsEnabledGlobal } = useSelector((state) => state.pmsSchedule);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const dentallyConfigs = useSelector((state) => state.practiceIntegration?.dentallyConfigs?.results || []);
    const configExportTrigger = dentallyConfigs.find((c) => c.is_active)?.dentally_export_trigger || 'APPOINTMENT';

    // Load Dentally config if not already in Redux
    useEffect(() => {
        if (practiceId && dentallyConfigs.length === 0) {
            dispatch(getDentallyConfigs(practiceId));
        }
    }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const [exportTriggerOverride, setExportTriggerOverride] = useState(null);
    const exportTrigger = exportTriggerOverride || configExportTrigger;

    const {
        toggle: openRecordAttend,
        onClose: onCloseRecordAttend
    } = useRecordAttendToggle();
    const {
        toggle: openUnRecordAttend,
        onClose: onCloseUnRecordAttend
    } = useUnRecordAttendToggle();

    const handleAttendClose = () => onCloseRecordAttend();
    const handleUnAttendClose = () => onCloseUnRecordAttend();

    const inputRef = useRef();
    const [validStep, setValidStep] = useState(false);
    const [active, setActive] = useState(0);
    // Track follow-up mode in a ref so it survives clearLead() dispatched by
    // OpenContactLeadPopup's parallel handleClose chain before the thunk callback fires.
    const isFollowUpRef = useRef(false);

    const [dentallySlot, setDentallySlot] = useState(null);
    const [dentallyPayment, setDentallyPayment] = useState({ paymentOption: 'none' });
    const [selectedDentallyTreatment, setSelectedDentallyTreatment] = useState(null);
    // PMS scheduling state
    const [pmsSlot, setPmsSlot] = useState(null);
    const [pmsPractitioner, setPmsPractitioner] = useState(null);
    const [pmsDate, setPmsDate] = useState(null);
    const [pmsConfirmStepState, setPmsConfirmStepState] = useState({});
    const [dentallyTreatmentName, setDentallyTreatmentName] = useState(null);
    const [dentallyReason, setDentallyReason] = useState('');
    const [dentallyAssignees, setDentallyAssignees] = useState([]);
    // Booking mode toggle — allows switching between Dentally and CRM-only flows
    const [useDentallyFlow, setUseDentallyFlow] = useState(true);
    const handleToggleBookingMode = useCallback((useDentally) => {
        setUseDentallyFlow(useDentally);
        if (!useDentally) {
            // Reset Dentally-specific state when switching to CRM-only
            setDentallySlot(null);
            setSelectedDentallyTreatment(null);
            setDentallyTreatmentName(null);
            setDentallyAssignees([]);
            setAvailSearchState({ selectedPractitioners: [], results: [], hasSearched: false, timeframePreset: '7days', duration: 30 });
        }
    }, []);

    // Availability search state — lifted here so it persists across wizard step navigation
    const [availSearchState, setAvailSearchState] = useState({
        selectedPractitioners: [],
        results: [],
        hasSearched: false,
        timeframePreset: '7days',
        duration: 30,
    });

    const {
        submit: dentallySubmit,
        result: dentallyResult,
        reset: dentallyReset,
        loading: dentallyLoading,
        error: dentallyError,
        bookingStep,
    } = useDentallyBooking({
        practiceId,
        existingLead: null,
        existingTreatmentId: null,
        isPaymentRequired: exportTrigger === 'PAYMENT',
    });

    const handleClose = (status, id, treatmentId) => {
        if (status?.toLowerCase?.() === 'finish') {
            const isFollowUp = isFollowUpRef.current;
            setTimeout(() => {
                const storeState = store.getState();
                const { sendReminderDuringLeadCreate: shouldSendReminder } = storeState.lead;
                const currentPracticeId = storeState.practiceTreatmentList?.practiceId;
                if (shouldSendReminder) {
                    const details = {
                        additional_notes: "",
                        contacted_via: "PHONE",
                        lead: id,
                        note: "UNABLE_TO_CONTACT",
                        treatment: treatmentId
                    }
                    dispatch(addNotes(details, currentPracticeId, '', 'createLead'));
                    dispatch(updateSendReminderDuringLeadCreate(false))
                }
                const successMessage = isFollowUp
                    ? 'Follow-up recorded successfully!'
                    : 'Lead successfully created! Refresh the page or click \'Show Lead\' to view and manage it.';
                dispatch(updateError({ success: true, message: successMessage, lead: id, timeout: 10000 }));
            }, 5000);
        }
        isFollowUpRef.current = false;
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        dispatch(updateSteps(null));
        dispatch(closeCreateLeadDialog());
        setDentallySlot(null);
        setDentallyPayment({ paymentOption: 'none' });
        setSelectedDentallyTreatment(null);
        setDentallyTreatmentName(null);
        setDentallyReason('');
        setDentallyAssignees([]);
        setAvailSearchState({ selectedPractitioners: [], results: [], hasSearched: false, timeframePreset: '7days', duration: 30 });
        setExportTriggerOverride(null);
        setUseDentallyFlow(true);
        dentallyReset();
    };

    // Called when user clicks "Close" on the Dentally booking success screen
    const handleDentallyClose = () => {
        // Save values before cleanup clears them
        const savedLeadId = dentallyResult?.leadId;
        const savedWarning = dentallyResult?.dentallyWarning;
        const savedPracticeId = practiceId; // capture before any cleanup

        // Cleanup state immediately so dialog closes
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        dispatch(updateSteps(null));
        dispatch(closeCreateLeadDialog());
        setDentallySlot(null);
        setDentallyPayment({ paymentOption: 'none' });
        setSelectedDentallyTreatment(null);
        setDentallyTreatmentName(null);
        setDentallyReason('');
        setDentallyAssignees([]);
        setAvailSearchState({ selectedPractitioners: [], results: [], hasSearched: false, timeframePreset: '7days', duration: 30 });
        setExportTriggerOverride(null);
        setUseDentallyFlow(true);
        dentallyReset();

        // Show toast with "Show lead" — small delay so backend fully commits
        if (savedLeadId != null && savedPracticeId) {
            setTimeout(() => {
                dispatch(updateError({
                    success: true,
                    message: `Booking confirmed! Lead #${savedLeadId} created and appointment booked.${savedWarning ? ` Note: ${savedWarning}` : ''}`,
                    lead: savedLeadId,
                    timeout: 10000,
                }));
            }, 2000);
        }
    };

    const onSubmit = (data) => setValidStep(!!data);

    const STEPS = [
        {
            label: 'Personal details',
            component: <PersonalDetails ref={inputRef} handleNext={onSubmit} active={active} />,
        },
    ];

    // ── Confirm button label + icon based on payment option ────────────────────
    const CONFIRM_MAP = {
        received: { label: 'Confirm & Record Payment', icon: 'mdi:cash-check' },
        link: { label: 'Confirm & Send Link', icon: 'mdi:send-check' },
    };
    const { label: confirmLabel, icon: confirmIcon } = CONFIRM_MAP[dentallyPayment.paymentOption] || { label: 'Confirm Booking', icon: 'mdi:check-bold' };

    if (pmsEnabledGlobal && !dentallyEnabledGlobal) {
        STEPS.push({
            label: 'Select Appointment',
            component: (
                <PmsAvailabilityWizardStep
                    ref={inputRef}
                    handleNext={onSubmit}
                    practiceId={practiceId}
                    pmsSlot={pmsSlot}
                    onSlotSelected={setPmsSlot}
                    selectedPractitioner={pmsPractitioner}
                    onPractitionerSelected={setPmsPractitioner}
                    selectedDate={pmsDate}
                    onDateSelected={setPmsDate}
                />
            ),
        });
        STEPS.push({
            label: 'Confirm Booking',
            ...pmsConfirmStepState,
            component: (
                <PmsBookingConfirmStep
                    ref={inputRef}
                    pmsSlot={pmsSlot}
                    practiceId={practiceId}
                    existingLead={null}
                    existingTreatmentId={null}
                    onClose={handleClose}
                    onStepStateChange={setPmsConfirmStepState}
                />
            ),
        });
    } else if (dentallyEnabledGlobal && useDentallyFlow) {
        STEPS.push({
            label: 'Treatment & Availability',
            component: (
                <Box>
                    <BookingModeToggle useDentally onChange={handleToggleBookingMode} />
                    <DentallyTreatmentAvailabilityWizardStep
                        ref={inputRef}
                        handleNext={onSubmit}
                        practiceId={practiceId}
                        dentallySlot={dentallySlot}
                        onSlotSelected={setDentallySlot}
                        selectedDentallyTreatment={selectedDentallyTreatment}
                        assignees={dentallyAssignees}
                        onAssigneesChanged={setDentallyAssignees}
                        availSearchState={availSearchState}
                        onAvailSearchStateChange={setAvailSearchState}
                        onTreatmentSelected={(treatment, crmName, treatmentPrice, appointmentReason) => {
                            setSelectedDentallyTreatment(treatment);
                            setDentallyTreatmentName(
                                crmName || (treatment ? (treatment.nomenclature || treatment.name) : null)
                            );
                            // Auto-populate payment amount from treatment price
                            if (treatmentPrice > 0) {
                                setDentallyPayment(prev => ({ ...prev, paymentAmount: treatmentPrice }));
                            }
                            // Auto-fill reason from synced appointment reason mapping
                            if (appointmentReason) setDentallyReason(appointmentReason);
                        }}
                    />
                </Box>
            ),
        });
        STEPS.push({
            label: 'Confirm Booking',
            buttonLabel: confirmLabel,
            buttonIcon: confirmIcon,
            buttonDisabled: !dentallySlot || dentallySlot._manual || dentallyLoading || dentallyResult?.success || (exportTrigger === 'PAYMENT' && dentallyPayment.paymentOption === 'none'),
            buttonLoading: dentallyLoading,
            hideFooterOnSuccess: dentallyResult?.success,
            component: (
                <DentallyPaymentWizardStep
                    ref={inputRef}
                    dentallySlot={dentallySlot}
                    exportTrigger={exportTrigger}
                    onExportTriggerChange={setExportTriggerOverride}
                    dentallyPayment={dentallyPayment}
                    onPaymentChange={setDentallyPayment}
                    dentallySubmit={dentallySubmit}
                    dentallyTreatmentName={dentallyTreatmentName}
                    dentallyReason={dentallyReason}
                    dentallyAssignees={dentallyAssignees}
                    dentallyLoading={dentallyLoading}
                    dentallyResult={dentallyResult}
                    dentallyError={dentallyError}
                    bookingStep={bookingStep}
                    onClose={handleDentallyClose}
                />
            ),
        });
    } else {
        STEPS.push({
            label: 'Treatment & Appointment',
            component: (
                <Box>
                    {dentallyEnabledGlobal && <BookingModeToggle useDentally={false} onChange={handleToggleBookingMode} />}
                    <TreatmentAndAppointment type={'CreatLead'} ref={inputRef} handleNext={onSubmit} active={active} hideDentally />
                </Box>
            ),
        });
    }

    const [leadStep, setLeadStep] = useState(STEPS);

    useEffect(() => {
        if (attendance) {
            const steps = [...STEPS];
            if (attendance === 'attendance') {
                steps.push({
                    label: 'Appointment Outcome',
                    component: <AttendedAppointment ref={inputRef} handleNext={onSubmit} active={active} type={'CreatedLead'} />
                });
            } else {
                steps.push({
                    label: 'Payment options',
                    component: <PaymentOption ref={inputRef} handleNext={onSubmit} active={active} />
                });
                if (dentallyEnabledGlobal && isDentallyBooking) {
                    steps.push({
                        label: 'Dentally Confirmation',
                        component: <DentallyPaymentConfirmation ref={inputRef} handleNext={onSubmit} />
                    });
                }
                steps.push({
                    label: 'Message review & reminders',
                    component: <MessageAndReminder ref={inputRef} handleNext={onSubmit} active={active} type={'CreatLead'} />
                });
            }
            setLeadStep(steps);
        } else {
            setLeadStep(STEPS);
        }
    }, [attendance, isDentallyBooking, useDentallyFlow]);

    // Keep step components fresh when slot, payment, or booking state changes
    useEffect(() => {
        if (attendance) return;
        setLeadStep(STEPS);
    }, [dentallySlot, dentallyPayment, selectedDentallyTreatment, dentallyTreatmentName, dentallyAssignees, dentallyLoading, dentallyResult, dentallyError, bookingStep, availSearchState, useDentallyFlow, pmsSlot, pmsPractitioner, pmsDate, pmsConfirmStepState]);

    useEffect(() => {
        if (!markAsComplete || consentLink || !openContacts) return;
        if (dentallyEnabledGlobal && useDentallyFlow) return; // Dentally submission is handled by DentallyPaymentWizardStep
        if (pmsEnabledGlobal && !dentallyEnabledGlobal) return; // PMS submission is handled by PmsBookingConfirmStep
        if (!followUpLead) {
            isFollowUpRef.current = false;
            dispatch(createNewLead(handleClose));
        } else if (followUpLead === 'followUp') {
            isFollowUpRef.current = true;
            dispatch(updateLeadDetail('finish', handleClose));
        }
    }, [markAsComplete, followUpLead, consentLink, openContacts]);

    const handleClickNewLead = () => {
        localStorageRemoveItem('localTreatmentData');
        dispatch(openCreateLeadDialog());
        dispatch(setCurrentTreatmentDataGlobal(null));
    };

    return (
        <>
            {openContacts && <OpenDialogPopup
                open={openContacts}
                onClose={handleClose}
                steps={leadStep}
                type={'createLead'}
                validStep={validStep}
                updateStep={(type) => setValidStep(type)}
                inputRef={inputRef}
                title='Create Lead'
                headerButtons
                setActive={setActive}
            />}
            {openRecordAttend && <OpenAttendedPopup
                open={openRecordAttend}
                onClose={handleAttendClose}
                type={'createLead'}
                title='Create Lead'
                headerButtons
            />}
            {openUnRecordAttend && <OpenUnAttendedPopup
                open={openUnRecordAttend}
                onClose={handleUnAttendClose}
                type={'createLead'}
                title='Create Lead'
                headerButtons
            />}
            <DentallyBookingForm
                open={dentallyBookingOpen}
                onClose={() => dispatch(closeDentallyBookingDialog())}
                existingLead={null}
            />
            <Box id="new-lead-button" onClick={handleClickNewLead} sx={{ visibility: "hidden", position: "absolute" }} />
        </>
    );
};

export default CreateLeadDialogWrapper;
